import React from "react";
import { Manager, Reference, Popper, PopperProps } from "react-popper";
import { SxStyleProp } from "theme-ui";
import { Omit } from "typings";
import Box from "../Box/Box";

const PopoverContext = React.createContext<PopoverContext>({});
const usePopoverContext = () => React.useContext(PopoverContext);

interface PopoverContext {
  isOpen?: boolean;
  onOpen?: (event?: any) => void;
  onClose?: (event?: any) => void;
  onToggle?: (event?: any) => void;
}

interface Props extends Omit<PopperProps, "children">, PopoverContext {
  sx?: SxStyleProp;
}

export const Popover: React.FunctionComponent<Props> = ({
  sx,
  isOpen,
  onOpen,
  onClose,
  onToggle,
  children,
}) => {
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const handleOuterClick = ({ target }: MouseEvent) => {
    const node = dropdownRef.current;
    if (node && !node.contains(target as HTMLElement)) {
      onClose && onClose();
    }
  };
  React.useEffect(() => {
    document.addEventListener("mousedown", handleOuterClick);
    return () => {
      document.removeEventListener("mousedown", handleOuterClick);
    };
  }, []);
  return (
    <PopoverContext.Provider value={{ isOpen, onOpen, onClose, onToggle }}>
      <Box
        ref={dropdownRef}
        sx={{
          position: "relative",
          ...(sx as any),
        }}
      >
        <Manager>{children}</Manager>
      </Box>
    </PopoverContext.Provider>
  );
};

export const PopoverContent: React.FunctionComponent<{
  initialPlacement?: PopperProps["placement"];
  sx?: SxStyleProp;
}> = ({ sx, initialPlacement = "bottom", children, ...restProps }) => {
  const { isOpen } = usePopoverContext();
  if (!isOpen) {
    return null;
  }

  return (
    <Popper placement={initialPlacement} {...restProps}>
      {({ ref, style, placement }) => (
        <Box
          ref={ref}
          style={style}
          data-placement={placement}
          sx={{
            width: "100%",
            zIndex: "popover",
            ...(sx as any),
          }}
        >
          {children}
        </Box>
      )}
    </Popper>
  );
};

export const PopoverTrigger: React.FunctionComponent<
  React.HTMLAttributes<HTMLElement>
> = ({ children, ...restProps }) => {
  return (
    <Reference>
      {({ ref }) => (
        <Box ref={ref} {...restProps}>
          {children}
        </Box>
      )}
    </Reference>
  );
};
